import { useState } from 'react';
import PropTypes from 'prop-types';
import {
  AlignCenter, AlignLeft, AlignRight, ChevronDown, ChevronUp,
  GripVertical, Plus, Settings2, Trash2, X,
} from 'lucide-react';
import {
  FIELD_TYPES, ITEM_COLUMNS, TEXT_TOKENS, TOTAL_PARTS, makeField,
} from '../data/billFields';

/**
 * Builds the list of fields a bill design is made of.
 *
 * The design before this was a fixed set of switches — the shop could turn my
 * lines on and off and nothing else. They could not add a line of their own,
 * could not reorder anything, and anything I had not thought of needed a code
 * change. Here they compose the receipt: which fields, in what order, each with
 * its own wording and alignment.
 */

const Chip = ({ active, onClick, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider border transition-colors ${
      active
        ? 'text-white border-transparent'
        : 'bg-white text-slate-500 border-[var(--theme-tertiary)] hover:bg-slate-50'
    }`}
    style={active ? { background: 'var(--theme-primary)' } : undefined}
  >
    {children}
  </button>
);
Chip.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node, title: PropTypes.string };

/**
 * What the inline box shows when it is empty — the value that will print if
 * nothing is typed. Naming the actual organization name beats "Leave empty to
 * use your organization name", because it shows what that name is.
 */
const placeholderFor = (field, org = {}) => {
  switch (field.type) {
    case 'shopName': return org.name || 'Your shop name';
    case 'address': return org.address || 'Your shop address';
    case 'phone': return org.phone || org.mobileNumber || 'Your shop phone';
    case 'branch': return "The branch's own name";
    case 'itemCount': return 'Total items';
    case 'title': return 'RECEIPT';
    default: return 'Type anything';
  }
};

const Row = ({ children, label }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">{label}</span>
    {children}
  </label>
);
Row.propTypes = { children: PropTypes.node, label: PropTypes.string };

/** The per-field settings, shown only for the field being edited. */
const FieldOptions = ({ field, onChange }) => {
  const spec = FIELD_TYPES[field.type] || {};
  const set = (patch) => onChange({ ...field, ...patch });

  const toggleIn = (key, value, fallback) => {
    const list = field[key] || fallback || [];
    set({ [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] });
  };

  return (
    <div className="px-3 pb-3 pt-1 flex flex-col gap-3 bg-slate-50/70 border-t border-slate-100">
      {/* Divider and blank lines have no wording and no alignment. Labelled,
          because three unlabelled icons beside two word buttons read as one
          undifferentiated row of controls. */}
      {field.type !== 'divider' && field.type !== 'blank' && (
        <div className="flex items-start gap-5 flex-wrap">
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Position</span>
            <div className="flex items-center gap-1">
              <Chip active={field.align === 'left'} onClick={() => set({ align: 'left' })} title="Left"><AlignLeft size={11} /></Chip>
              <Chip active={field.align === 'center'} onClick={() => set({ align: 'center' })} title="Centre"><AlignCenter size={11} /></Chip>
              <Chip active={field.align === 'right'} onClick={() => set({ align: 'right' })} title="Right"><AlignRight size={11} /></Chip>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">Size</span>
            <div className="flex items-center gap-1">
              <Chip active={!!field.bold} onClick={() => set({ bold: !field.bold })}>Bold</Chip>
              <Chip active={!!field.big} onClick={() => set({ big: !field.big })}>Big</Chip>
            </div>
          </div>
        </div>
      )}

      {field.type === 'text' && (
        <span className="text-[10px] text-slate-400 leading-relaxed">
          Fixed words print as typed. These fill in per bill:{' '}
          <span className="font-mono text-slate-500">{TEXT_TOKENS.join(' ')}</span>
        </span>
      )}

      {spec.countMode && (
        <div className="flex items-center gap-1">
          <Chip active={field.mode !== 'qty'} onClick={() => set({ mode: 'lines' })}>Different items</Chip>
          <Chip active={field.mode === 'qty'} onClick={() => set({ mode: 'qty' })}>Total quantity</Chip>
        </div>
      )}

      {spec.columns && (
        <Row label="Columns beside the item name">
          <div className="flex items-center gap-1 flex-wrap">
            {Object.entries(ITEM_COLUMNS).map(([key, col]) => (
              <Chip
                key={key}
                active={(field.columns || []).includes(key)}
                onClick={() => toggleIn('columns', key)}
              >
                {col.label}
              </Chip>
            ))}
          </div>
          <span className="text-[10px] text-slate-400">
            Turn off Amount and the item name takes that space — that is what makes a kitchen
            ticket, with no money on it.
          </span>
        </Row>
      )}

      {spec.parts && (
        <Row label="Which totals to print">
          <div className="flex items-center gap-1 flex-wrap">
            {Object.entries(TOTAL_PARTS).map(([key, label]) => (
              <Chip
                key={key}
                active={(field.parts || []).includes(key)}
                onClick={() => toggleIn('parts', key)}
              >
                {label}
              </Chip>
            ))}
          </div>
        </Row>
      )}

      {spec.code && (
        <>
          <Row label={spec.codeLabel || 'What the code holds'}>
            <input
              value={field.url || ''}
              onChange={(e) => set({ url: e.target.value })}
              placeholder={spec.codePlaceholder}
              className="bg-white border border-[var(--theme-tertiary)] rounded-md px-3 py-2 text-xs outline-none focus:border-[var(--theme-primary)] font-mono"
            />
            <span className="text-[10px] text-slate-400 leading-relaxed">
              Put <span className="font-mono text-slate-500">{'{billNo}'}</span> where the bill
              number should go. The thermal printer draws the code itself, so it stays sharp.
              {field.type === 'barcode' && ' A barcode holds plain letters and numbers only — no links.'}
            </span>
          </Row>
          <Row label="Words under the code">
            <input
              value={field.text || ''}
              onChange={(e) => set({ text: e.target.value })}
              placeholder={field.type === 'barcode' ? 'Bill number' : 'Scan to return an item'}
              className="bg-white border border-[var(--theme-tertiary)] rounded-md px-3 py-2 text-xs outline-none focus:border-[var(--theme-primary)]"
            />
          </Row>
        </>
      )}
    </div>
  );
};
FieldOptions.propTypes = { field: PropTypes.object, onChange: PropTypes.func };

/** The menu of field types, grouped so a long list stays findable. */
const AddFieldMenu = ({ onAdd, onClose }) => {
  const groups = {};
  Object.entries(FIELD_TYPES).forEach(([type, spec]) => {
    (groups[spec.group] = groups[spec.group] || []).push({ type, spec });
  });

  return (
    <div className="fixed inset-0 z-[950] bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <p className="text-sm font-bold text-[#0d0d0d]">Add a field</p>
          <button type="button" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="p-4 overflow-y-auto flex flex-col gap-4">
          {Object.entries(groups).map(([group, items]) => (
            <div key={group}>
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-2">{group}</p>
              {/* Said once, where someone looking for a field they cannot find
                  will read it. The list can never cover every trade's paperwork;
                  "Your own line" is how it covers all of them. */}
              {group === 'Extra' && (
                <p className="text-[10px] text-slate-400 leading-relaxed mb-2">
                  Not on the list? <strong>Your own line</strong> prints anything you type — a PAN
                  number, a licence number, a note.
                </p>
              )}
              <div className="grid grid-cols-2 gap-2">
                {items.map(({ type, spec }) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => { onAdd(type); onClose(); }}
                    className="text-left px-3 py-2 rounded-lg border border-[var(--theme-tertiary)] text-xs font-semibold text-[#0d0d0d] hover:border-[var(--theme-primary)] hover:bg-[var(--theme-secondary)] transition-colors"
                  >
                    {spec.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
AddFieldMenu.propTypes = { onAdd: PropTypes.func, onClose: PropTypes.func };

export const BillFieldEditor = ({ fields, onChange, org = {} }) => {
  const [openId, setOpenId] = useState(null);
  const [adding, setAdding] = useState(false);
  const [dragFrom, setDragFrom] = useState(null);

  const replace = (id, next) => onChange(fields.map((f) => (f.id === id ? next : f)));
  const remove = (id) => {
    onChange(fields.filter((f) => f.id !== id));
    if (openId === id) setOpenId(null);
  };

  const move = (from, to) => {
    if (to < 0 || to >= fields.length) return;
    const next = [...fields];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  // Only for fields whose value is not a plain string; the rest carry an inline
  // box on the row that already shows it.
  const summaryOf = (f) => {
    if (f.type === 'items') return (f.columns || []).map((c) => ITEM_COLUMNS[c]?.label).filter(Boolean).join(', ') || 'name only';
    if (f.type === 'totals') return (f.parts || []).map((p) => TOTAL_PARTS[p]).filter(Boolean).join(', ') || 'nothing chosen';
    if (f.type === 'qr' || f.type === 'barcode') return f.url || 'nothing set';
    return '';
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Fields, top to bottom</p>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider"
          style={{ color: 'var(--theme-primary)' }}
        >
          <Plus size={12} /> Add field
        </button>
      </div>

      {/* One bordered list with ruled rows, rather than fifteen separate cards
          with a border and a gap each. Fifteen cards is fifteen boxes to read
          past before reaching the one being looked for. */}
      <div className="rounded-lg border border-[var(--theme-tertiary)] overflow-hidden divide-y divide-slate-100">
        {fields.map((f, i) => {
          const spec = FIELD_TYPES[f.type] || { label: f.type };
          const summary = summaryOf(f);
          const isOpen = openId === f.id;
          const inlineText = !!spec.text && !spec.code;
          return (
            <div
              key={f.id}
              // Dragging is the quick way with a mouse. It does not exist on
              // touch, so the arrows do the same job and are always there.
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) move(dragFrom, i); setDragFrom(null); }}
              className={isOpen ? 'bg-[var(--theme-secondary)]' : 'bg-white hover:bg-slate-50/60 transition-colors'}
            >
              <div className="flex items-center gap-2 px-2.5 py-1.5">
                <span
                  draggable
                  onDragStart={() => setDragFrom(i)}
                  onDragEnd={() => setDragFrom(null)}
                  className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-400 shrink-0"
                  title="Drag to move"
                >
                  <GripVertical size={13} />
                </span>

                <div className="min-w-0 flex-1">
                  <p className={`text-[11px] font-bold truncate ${f.on === false ? 'text-slate-300 line-through' : 'text-[#0d0d0d]'}`}>
                    {spec.label}
                  </p>
                  {/* The value is typed on the row, not hidden behind ⚙. Reading
                      "Shop name — from your organization" and having to open a
                      settings panel to change it is a step too many for the one
                      thing on that row anybody wants to change. ⚙ keeps what is
                      genuinely secondary: alignment, bold, size. */}
                  {inlineText ? (
                    <input
                      value={f.text || ''}
                      onChange={(e) => replace(f.id, { ...f, text: e.target.value })}
                      placeholder={placeholderFor(f, org)}
                      disabled={f.on === false}
                      aria-label={`${spec.label} text`}
                      className="w-full bg-transparent border-b border-dashed border-slate-200 text-[10px] text-slate-600 placeholder:text-slate-300 outline-none focus:border-[var(--theme-primary)] focus:border-solid disabled:text-slate-300"
                    />
                  ) : (
                    summary && <p className="text-[10px] text-slate-400 truncate">{summary}</p>
                  )}
                </div>

                {/* Deliberately small. There is one of these on every row, and
                    at full size a column of them shouted over the field names
                    they belong to. */}
                <button
                  type="button"
                  onClick={() => replace(f.id, { ...f, on: f.on === false })}
                  className={`shrink-0 w-7 h-4 rounded-full transition-colors relative ${f.on === false ? 'bg-slate-200' : 'bg-[var(--theme-primary)]'}`}
                  role="switch"
                  aria-checked={f.on !== false}
                  aria-label={`Print ${spec.label}`}
                >
                  <span className={`absolute top-0.5 left-0.5 bg-white w-3 h-3 rounded-full shadow-sm transition-transform ${f.on === false ? '' : 'translate-x-3'}`} />
                </button>

                {/* Kept visible rather than revealed on hover: a till is a
                    touch screen as often as not, and there is no hovering
                    there. Made quieter instead — lighter, tighter, smaller. */}
                <div className="flex items-center shrink-0 text-slate-300">
                  <button type="button" onClick={() => move(i, i - 1)} disabled={i === 0} className="h-6 w-5 grid place-items-center rounded hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30" aria-label="Move up">
                    <ChevronUp size={12} />
                  </button>
                  <button type="button" onClick={() => move(i, i + 1)} disabled={i === fields.length - 1} className="h-6 w-5 grid place-items-center rounded hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30" aria-label="Move down">
                    <ChevronDown size={12} />
                  </button>
                  <button type="button" onClick={() => setOpenId(isOpen ? null : f.id)} className={`h-6 w-6 grid place-items-center rounded hover:bg-slate-100 ${isOpen ? 'text-[var(--theme-primary)]' : 'hover:text-slate-600'}`} aria-label={`Settings for ${spec.label}`}>
                    <Settings2 size={12} />
                  </button>
                  <button type="button" onClick={() => remove(f.id)} className="h-6 w-6 grid place-items-center rounded hover:text-rose-600 hover:bg-rose-50" aria-label={`Remove ${spec.label}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>

              {isOpen && <FieldOptions field={f} onChange={(next) => replace(f.id, next)} />}
            </div>
          );
        })}

        {fields.length === 0 && (
          <p className="text-[11px] text-slate-400 py-6 text-center bg-white">
            No fields yet. Press <strong>Add field</strong> to start.
          </p>
        )}
      </div>

      {adding && (
        <AddFieldMenu
          onAdd={(type) => onChange([...fields, makeField(type)])}
          onClose={() => setAdding(false)}
        />
      )}
    </div>
  );
};

BillFieldEditor.propTypes = {
  fields: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  org: PropTypes.object,
};
